import { http } from '@/network/http';

export interface MediaAssetDTO {
  id: string;
  filename: string;
  mimeType: string;
  r2Key: string;
  publicUrl: string;
  fileSize: number;
  durationMs: number;
  width: number;
  height: number;
  mediaType: string;
  thumbnailUrl: string | null;
  status: string;
  createdAt: string;
}

export interface StorageInfo {
  storageUsed: number;
  storageQuota: number;
}

/** Fetch all media assets for the current user */
export async function fetchMediaAssets(): Promise<MediaAssetDTO[]> {
  const res = await http.get('/media');
  return res.data.assets;
}

/** Create a media record after R2 upload */
export async function createMediaAsset(data: {
  filename: string;
  mimeType: string;
  r2Key: string;
  publicUrl: string;
  fileSize: number;
  durationMs?: number;
  width?: number;
  height?: number;
  mediaType?: string;
  thumbnailUrl?: string;
  thumbnailKey?: string;
}): Promise<MediaAssetDTO> {
  const res = await http.post('/media', data);
  return res.data.asset;
}

/** Delete a media asset */
export async function deleteMediaAsset(id: string): Promise<void> {
  await http.delete(`/media/${id}`);
}

/** Get storage usage */
export async function fetchStorageInfo(): Promise<StorageInfo> {
  const res = await http.get('/media/storage');
  return res.data;
}

/** Presign an upload URL */
export async function presignUpload(filename: string, mimeType: string, purpose = 'media'): Promise<{
  uploadUrl: string;
  key: string;
  publicUrl: string;
}> {
  const res = await http.post('/uploads/presign', { filename, mimeType, purpose });
  return res.data;
}

/** Upload file to the presigned URL */
export async function uploadToR2(
  uploadUrl: string,
  file: File,
  onProgress?: (pct: number) => void,
): Promise<{ key: string; size: number }> {
  const res = await http.put(uploadUrl, file, {
    headers: { 'Content-Type': file.type },
    onUploadProgress: onProgress
      ? (e: any) => onProgress(Math.round((e.loaded / (e.total || file.size)) * 100))
      : undefined,
  });
  return res.data;
}

/**
 * Full upload pipeline: presign → upload to R2 → capture thumbnail → upload thumbnail → create media record
 */
export async function uploadMediaFile(
  file: File,
  onProgress?: (pct: number) => void,
): Promise<MediaAssetDTO> {
  // 1. Presign main file
  const { uploadUrl, key, publicUrl } = await presignUpload(file.name, file.type);

  // 2. Upload main file to R2
  await uploadToR2(uploadUrl, file, onProgress);

  // 3. Probe duration/dimensions
  const probe = await probeMediaFile(file);

  // 4. Capture thumbnail + upload to R2 (for video files)
  let thumbnailUrl: string | undefined;
  let thumbnailKey: string | undefined;

  if (file.type.startsWith('video/')) {
    try {
      const thumbBlob = await captureVideoThumbnail(file);
      if (thumbBlob) {
        const thumbFilename = file.name.replace(/\.[^.]+$/, '_thumb.jpg');
        const thumbPresign = await presignUpload(thumbFilename, 'image/jpeg', 'thumbnail');
        await uploadToR2(thumbPresign.uploadUrl, new File([thumbBlob], thumbFilename, { type: 'image/jpeg' }));
        thumbnailUrl = thumbPresign.publicUrl;
        thumbnailKey = thumbPresign.key;
      }
    } catch {
      // Thumbnail capture failed — continue without it
    }
  }

  // 5. Create record
  return createMediaAsset({
    filename: file.name,
    mimeType: file.type,
    r2Key: key,
    publicUrl,
    fileSize: file.size,
    durationMs: probe.durationMs,
    width: probe.width,
    height: probe.height,
    mediaType: file.type.startsWith('audio/') ? 'audio' : 'video',
    thumbnailUrl,
    thumbnailKey,
  });
}

/** Capture a JPEG thumbnail from a video file at ~1s */
function captureVideoThumbnail(file: File): Promise<Blob | null> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    video.preload = 'auto';
    video.muted = true;
    video.playsInline = true;
    video.crossOrigin = 'anonymous';

    const timeout = setTimeout(() => { cleanup(); resolve(null); }, 10000);
    let seeked = false;

    function cleanup() {
      clearTimeout(timeout);
      video.removeAttribute('src');
      video.load();
      URL.revokeObjectURL(url);
    }

    function drawFrame() {
      if (seeked) return; // prevent double-fire
      seeked = true;
      try {
        const W = 320;
        const H = 180;
        const canvas = document.createElement('canvas');
        canvas.width = W;
        canvas.height = H;
        const ctx = canvas.getContext('2d')!;

        // Letterbox
        const srcAspect = video.videoWidth / video.videoHeight;
        const dstAspect = W / H;
        let dw: number, dh: number, dx: number, dy: number;
        if (srcAspect > dstAspect) {
          dw = W; dh = W / srcAspect; dx = 0; dy = (H - dh) / 2;
        } else {
          dh = H; dw = H * srcAspect; dx = (W - dw) / 2; dy = 0;
        }
        ctx.fillStyle = '#1A1A1E';
        ctx.fillRect(0, 0, W, H);
        ctx.drawImage(video, dx, dy, dw, dh);

        cleanup();
        canvas.toBlob((blob) => resolve(blob), 'image/jpeg', 0.8);
      } catch (err) {
        console.warn('Thumbnail capture draw failed:', err);
        cleanup();
        resolve(null);
      }
    }

    video.onseeked = drawFrame;

    video.onloadeddata = () => {
      // Seek to 10% or 1s — whichever is smaller
      const seekTime = Math.min(1, video.duration * 0.1);
      if (seekTime > 0) {
        video.currentTime = seekTime;
      } else {
        // Very short video or duration unknown — draw first frame
        drawFrame();
      }
    };

    video.onerror = () => { cleanup(); resolve(null); };
    video.src = url;
  });
}

/** Probe a local file for duration and dimensions */
function probeMediaFile(file: File): Promise<{ durationMs: number; width: number; height: number }> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.muted = true;

    const timeout = setTimeout(() => {
      cleanup();
      resolve({ durationMs: 0, width: 0, height: 0 });
    }, 5000);

    function cleanup() {
      clearTimeout(timeout);
      video.src = '';
      URL.revokeObjectURL(url);
    }

    video.onloadedmetadata = () => {
      const result = {
        durationMs: Math.round(video.duration * 1000),
        width: video.videoWidth,
        height: video.videoHeight,
      };
      cleanup();
      resolve(result);
    };

    video.onerror = () => {
      cleanup();
      resolve({ durationMs: 0, width: 0, height: 0 });
    };

    video.src = url;
  });
}
